import { useState, useRef, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { CreditCard, Lock, Shield, Check } from 'lucide-react'
import Header from '../components/Header'
import ProcessingOverlay from '../components/ProcessingOverlay'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { showToast } from '../components/Toast'
import { isWeb3Mode } from '../lib/authMode'
import { hasWeb3Provider } from '../lib/ethers'

interface ChargeState {
  amount: number
  fee: number
  total: number
}

function formatYen(amount: number) {
  return amount.toLocaleString()
}

function detectBrand(number: string): 'visa' | 'mastercard' | 'amex' {
  const first = number.replace(/\D/g, '').charAt(0)
  if (first === '5') return 'mastercard'
  if (first === '3') return 'amex'
  return 'visa'
}

export default function ChargeConfirm() {
  const navigate = useNavigate()
  const location = useLocation()
  const { charge, cards, addCard } = useStore()
  const { t } = useI18n()

  const state = (location.state as ChargeState) ?? {
    amount: 10000,
    fee: 790,
    total: 10790,
  }

  const { amount, fee, total } = state

  const [isProcessing, setIsProcessing] = useState(false)
  const chargePromiseRef = useRef<Promise<void> | null>(null)

  // Determine if this will be an on-chain charge (backend mints TestJPYC)
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'
  const isOnChain = !isDemoMode && isWeb3Mode() && hasWeb3Provider()

  const processingSteps = useMemo(() => {
    if (isOnChain) {
      return [t('chargeConfirm.processing'), t('chargeConfirm.mintingOnChain'), t('chargeConfirm.confirmingMint')]
    }
    return [t('chargeConfirm.processing'), t('chargeConfirm.issuing'), t('chargeConfirm.transferring')]
  }, [isOnChain, t])

  const processingDuration = isOnChain ? 5000 : 1000

  // Pre-select the default card if saved cards exist, otherwise 'new'
  const defaultCard = cards.find((c) => c.isDefault)
  const [selectedCardId, setSelectedCardId] = useState<string>(
    defaultCard ? defaultCard.id : 'new'
  )

  // Controlled form state for new card
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242')
  const [cardExpiry, setCardExpiry] = useState('12/28')
  const [cardCvc, setCardCvc] = useState('123')
  const [cardName, setCardName] = useState('ALEX JOHNSON')
  const [saveCard, setSaveCard] = useState(true)

  // Ref to hold saveCard info for processing complete callback
  const saveCardRef = useRef({ saveCard, cardNumber, cardExpiry, cardName, selectedCardId })
  useEffect(() => {
    saveCardRef.current = { saveCard, cardNumber, cardExpiry, cardName, selectedCardId }
  }, [saveCard, cardNumber, cardExpiry, cardName, selectedCardId])

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16)
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
  }

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4)
    if (digits.length > 2) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`
    }
    return digits
  }

  const handlePay = () => {
    setIsProcessing(true)
    // Start API charge in background while the animation plays
    chargePromiseRef.current = charge(amount)
  }

  const handleProcessingComplete = async () => {
    const ref = saveCardRef.current
    // Save the new card if the user opted in
    if (ref.selectedCardId === 'new' && ref.saveCard) {
      const digits = ref.cardNumber.replace(/\D/g, '')
      if (digits.length >= 4) {
        addCard({
          brand: detectBrand(ref.cardNumber),
          last4: digits.slice(-4),
          expiry: ref.cardExpiry || '',
          name: ref.cardName || '',
          isDefault: false,
        })
        showToast({ type: 'success', message: t('card.added') })
      }
    }
    // Wait for the API charge to complete (should be done by now)
    try {
      await chargePromiseRef.current
      navigate('/charge/success')
    } catch {
      setIsProcessing(false)
      showToast({ type: 'error', message: 'Charge failed. Please try again.' })
    }
  }

  const isNewCard = cards.length === 0 || selectedCardId === 'new'

  return (
    <div className="relative flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('chargeConfirm.title')} onBack={() => navigate(-1)} />

      {/* Demo Mode Badge */}
      <div className="absolute right-4 top-3.5">
        <span className="rounded-full bg-gold-light px-3 py-1 text-xs font-bold text-text-primary">
          {t('chargeConfirm.demoMode')}
        </span>
      </div>

      {/* Summary Card */}
      <div className="mx-4 mt-4 rounded-xl bg-navy p-6 text-white">
        <span className="font-jp text-sm text-white/70">
          {t('chargeConfirm.chargeTo')}
        </span>
        <div className="mt-2 font-display text-[28px] font-bold text-gold">
          &yen;{formatYen(amount)}
        </div>
        <span className="mt-1 text-sm text-white/70">
          {t('chargeConfirm.inclFee', { fee: fee.toString() })}
        </span>
      </div>

      {/* Card Information Heading */}
      <h2 className="px-4 pt-6 pb-3 font-display text-base font-semibold text-text-primary">
        {cards.length > 0 ? t('card.selectCard') : t('chargeConfirm.cardInfo')}
      </h2>

      {/* Saved Card Selector */}
      {cards.length > 0 && (
        <div className="space-y-2 px-4 pb-4">
          {cards.map((card) => {
            const isSelected = selectedCardId === card.id
            const brandLabel =
              card.brand === 'visa'
                ? 'V'
                : card.brand === 'mastercard'
                  ? 'M'
                  : 'A'
            const brandColor =
              card.brand === 'mastercard'
                ? 'bg-red-500 text-white'
                : 'bg-blue-600 text-white'
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => setSelectedCardId(card.id)}
                className={`flex w-full items-center gap-3 rounded-xl border-2 bg-white px-4 py-3.5 text-left transition-colors ${
                  isSelected
                    ? 'border-yokoso-red'
                    : 'border-gray-200'
                }`}
              >
                {/* Radio circle */}
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    isSelected
                      ? 'border-yokoso-red'
                      : 'border-gray-300'
                  }`}
                >
                  {isSelected && (
                    <span className="h-2.5 w-2.5 rounded-full bg-yokoso-red" />
                  )}
                </span>
                {/* Brand indicator */}
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold ${brandColor}`}
                >
                  {brandLabel}
                </span>
                {/* Card details */}
                <span className="flex flex-1 items-center gap-2">
                  <span className="font-mono text-sm text-text-primary">
                    &bull;&bull;&bull;&bull; {card.last4}
                  </span>
                </span>
                {/* Default badge */}
                {card.isDefault && (
                  <span className="rounded-full bg-gold-light px-2 py-0.5 text-[10px] font-bold text-text-primary">
                    {t('card.default')}
                  </span>
                )}
              </button>
            )
          })}

          {/* Use a new card option */}
          <button
            type="button"
            onClick={() => setSelectedCardId('new')}
            className={`flex w-full items-center gap-3 rounded-xl border-2 bg-white px-4 py-3.5 text-left transition-colors ${
              selectedCardId === 'new'
                ? 'border-yokoso-red'
                : 'border-gray-200'
            }`}
          >
            {/* Radio circle */}
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                selectedCardId === 'new'
                  ? 'border-yokoso-red'
                  : 'border-gray-300'
              }`}
            >
              {selectedCardId === 'new' && (
                <span className="h-2.5 w-2.5 rounded-full bg-yokoso-red" />
              )}
            </span>
            {/* Plus icon area */}
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 text-text-tertiary">
              <CreditCard size={16} />
            </span>
            <span className="text-sm font-medium text-text-primary">
              {t('card.useNew')}
            </span>
          </button>
        </div>
      )}

      {/* Form Fields - shown when no saved cards or "use new card" is selected */}
      {isNewCard && (
        <div className="space-y-3 px-4">
          {/* Card Number */}
          <div className="relative">
            <CreditCard
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary"
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="1234 5678 9012 3456"
              value={cardNumber}
              onChange={(e) => {
                setCardNumber(formatCardNumber(e.target.value))
              }}
              className="h-[52px] w-full rounded-lg border border-gray-200 bg-white pl-12 pr-4 font-mono text-sm text-text-primary placeholder:text-text-disabled focus:border-yokoso-red focus:outline-none focus:ring-1 focus:ring-yokoso-red transition-colors"
            />
          </div>

          {/* Expiry + CVC Row */}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              inputMode="numeric"
              placeholder="MM/YY"
              value={cardExpiry}
              onChange={(e) => {
                setCardExpiry(formatExpiry(e.target.value))
              }}
              className="h-[52px] w-full rounded-lg border border-gray-200 bg-white px-4 font-mono text-sm text-text-primary placeholder:text-text-disabled focus:border-yokoso-red focus:outline-none focus:ring-1 focus:ring-yokoso-red transition-colors"
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="CVC"
              value={cardCvc}
              onChange={(e) => {
                setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))
              }}
              className="h-[52px] w-full rounded-lg border border-gray-200 bg-white px-4 font-mono text-sm text-text-primary placeholder:text-text-disabled focus:border-yokoso-red focus:outline-none focus:ring-1 focus:ring-yokoso-red transition-colors"
            />
          </div>

          {/* Name on Card */}
          <input
            type="text"
            placeholder={t('card.name')}
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            className="h-[52px] w-full rounded-lg border border-gray-200 bg-white px-4 font-body text-sm text-text-primary placeholder:text-text-disabled focus:border-yokoso-red focus:outline-none focus:ring-1 focus:ring-yokoso-red transition-colors"
          />

          {/* Save Card Toggle */}
          <button
            type="button"
            onClick={() => setSaveCard(!saveCard)}
            className="flex w-full items-center gap-3 py-2"
          >
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                saveCard
                  ? 'border-yokoso-red bg-yokoso-red'
                  : 'border-gray-300 bg-white'
              }`}
            >
              {saveCard && <Check size={14} className="text-white" strokeWidth={3} />}
            </span>
            <span className="font-body text-[13px] text-text-secondary">
              {t('card.saveForLater')}
            </span>
          </button>
        </div>
      )}

      {/* Secured by Stripe Badge */}
      <div className="mt-4 flex items-center justify-center gap-1.5">
        <Lock size={14} className="text-text-tertiary" />
        <span className="font-body text-xs text-text-tertiary">
          {t('chargeConfirm.securedBy')}
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* CTA Button */}
      <div className="px-4 pb-3 pt-4">
        <button
          onClick={handlePay}
          disabled={isProcessing}
          className="tap-effect h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button transition-colors hover:bg-yokoso-red-dark disabled:opacity-50"
        >
          {t('chargeConfirm.pay', { total: total.toLocaleString() })}
        </button>
      </div>

      {/* Security Note */}
      <div className="flex items-center justify-center gap-1.5 px-4 pb-6">
        <Shield size={14} className="text-text-tertiary" />
        <span className="font-body text-xs text-text-tertiary">
          {t('chargeConfirm.cardSafe')}
        </span>
      </div>

      {/* Processing Overlay */}
      <ProcessingOverlay
        visible={isProcessing}
        steps={processingSteps}
        stepDuration={processingDuration}
        onComplete={handleProcessingComplete}
      />
    </div>
  )
}
